import { Badge, Box, Flex, Text } from '@chakra-ui/react'
import { format, isValid, parseISO } from 'date-fns'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { BiSortAlt2, BiSortDown, BiSortUp } from 'react-icons/bi'

import type { IRecomendacaoComprasRow } from '@hook/mrp/use-pedidos-cronograma'

const fmt = (n: number | undefined | null) => {
  if (n == null) return '—'
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

const fmtPct = (
  pct: number | undefined | null
): { label: string; color: string } => {
  if (pct == null) return { label: '—', color: 'gray' }
  if (Math.abs(pct) < 0.01) return { label: '0%', color: 'gray' }
  const label = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
  const color = pct > 0 ? 'green' : 'red'
  return { label, color }
}

const fmtDate = (d: string | undefined | null) => {
  if (!d) return '—'
  const date = parseISO(d)
  return isValid(date) ? format(date, 'dd/MM/yyyy') : '—'
}

interface Column {
  id: string
  label: string
  align?: 'left' | 'right' | 'center'
  render: (row: IRecomendacaoComprasRow) => ReactNode
  sortKey?: (row: IRecomendacaoComprasRow) => string | number
}

const columns: Column[] = [
  {
    id: 'produtoNome',
    label: 'SKU',
    render: row => (
      <>
        <Text fontSize="13px" fontWeight="500">
          {row.produtoNome}
        </Text>
        <Text fontSize="10px" color="gray.400">
          #{row.produtoId}
        </Text>
      </>
    ),
    sortKey: row => row.produtoNome ?? ''
  },
  {
    id: 'fornecedorNome',
    label: 'Fornecedor',
    render: row => (
      <>
        <Text fontSize="13px">{row.fornecedorNome}</Text>
        <Text fontSize="10px" color="gray.400">
          #{row.fornecedorId}
        </Text>
      </>
    ),
    sortKey: row => row.fornecedorNome ?? ''
  },
  {
    id: 'compradorNome',
    label: 'Comprador',
    render: row => (
      <Text fontSize="13px">
        {row.compradorNome || row.compradorEmail || '—'}
      </Text>
    ),
    sortKey: row => row.compradorNome || row.compradorEmail || ''
  },
  {
    id: 'lojaNome',
    label: 'Centro',
    render: row => (
      <>
        <Text fontSize="13px">{row.lojaNome}</Text>
        <Text fontSize="10px" color="gray.400">
          #{row.lojaId}
        </Text>
      </>
    ),
    sortKey: row => row.lojaNome ?? ''
  },
  {
    id: 'recomendacaoQuantidade',
    label: 'Qtd Recomendada',
    align: 'right',
    render: row => (
      <Text fontSize="13px">{fmt(row.recomendacaoQuantidade)}</Text>
    ),
    sortKey: row => row.recomendacaoQuantidade ?? 0
  },
  {
    id: 'compraDesejada',
    label: 'Qtd Desejada',
    align: 'right',
    render: row => (
      <Text
        fontSize="13px"
        fontWeight={row.alteradoSimulacao ? '700' : 'normal'}
        color={row.alteradoSimulacao ? 'orange.700' : undefined}
      >
        {fmt(row.compraDesejada)}
      </Text>
    ),
    sortKey: row => row.compraDesejada ?? 0
  },
  {
    id: 'quantidadeAlteradaSimulacao',
    label: '\u0394',
    align: 'right',
    render: row => (
      <Text
        fontSize="13px"
        color={
          (row.quantidadeAlteradaSimulacao ?? 0) > 0
            ? 'green.600'
            : (row.quantidadeAlteradaSimulacao ?? 0) < 0
            ? 'red.600'
            : 'gray.500'
        }
      >
        {fmt(row.quantidadeAlteradaSimulacao)}
      </Text>
    ),
    sortKey: row => row.quantidadeAlteradaSimulacao ?? 0
  },
  {
    id: 'percentualAlteracaoSimulacao',
    label: '%',
    align: 'right',
    render: row => {
      const pct = fmtPct(row.percentualAlteracaoSimulacao)
      return pct.label === '—' ? (
        <Text fontSize="12px" color="gray.400">
          —
        </Text>
      ) : (
        <Flex justify="flex-end">
          <Badge
            colorScheme={pct.color}
            fontSize="10px"
            borderRadius="full"
            px={2}
          >
            {pct.label}
          </Badge>
        </Flex>
      )
    },
    sortKey: row => row.percentualAlteracaoSimulacao ?? 0
  },
  {
    id: 'recomendacaoTipoAlertaDescricao',
    label: 'Tipo Alerta',
    render: row => (
      <Text fontSize="12px" color="gray.600">
        {row.recomendacaoTipoAlertaDescricao ?? '—'}
      </Text>
    ),
    sortKey: row => row.recomendacaoTipoAlertaDescricao ?? ''
  },
  {
    id: 'data',
    label: 'Data',
    render: row => (
      <Text fontSize="12px" color="gray.500">
        {fmtDate(row.data)}
      </Text>
    ),
    sortKey: row => row.data ?? ''
  },
  {
    id: 'alteradoSimulacao',
    label: 'Alterado',
    align: 'center',
    render: row => (
      <Badge
        colorScheme={row.alteradoSimulacao ? 'orange' : 'gray'}
        fontSize="10px"
        borderRadius="full"
        px={2}
      >
        {row.alteradoSimulacao ? 'Sim' : 'Não'}
      </Badge>
    ),
    sortKey: row => (row.alteradoSimulacao ? 1 : 0)
  }
]

interface Props {
  data: IRecomendacaoComprasRow[]
  height?: string
}

const PedidosCronogramaTable: FC<Props> = ({ data, height }) => {
  const [sortCol, setSortCol] = useState<string>('')
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null)

  const handleSort = useCallback(
    (colId: string) => {
      if (sortCol === colId) {
        if (sortDir === 'desc') {
          setSortDir('asc')
        } else {
          setSortCol('')
          setSortDir(null)
        }
      } else {
        setSortCol(colId)
        setSortDir('desc')
      }
    },
    [sortCol, sortDir]
  )

  const sorted = useMemo(() => {
    if (!sortCol || !sortDir) return data
    const col = columns.find(c => c.id === sortCol)
    if (!col?.sortKey) return data

    return [...data].sort((a, b) => {
      const va = col.sortKey!(a)
      const vb = col.sortKey!(b)
      let cmp = 0
      if (typeof va === 'string' || typeof vb === 'string') {
        cmp = String(va).localeCompare(String(vb), 'pt-BR', {
          sensitivity: 'base',
          numeric: true
        })
      } else if (va > vb) cmp = 1
      else if (va < vb) cmp = -1
      return sortDir === 'desc' ? cmp * -1 : cmp
    })
  }, [data, sortCol, sortDir])

  return (
    <Box h={height || 'calc(100vh - 400px)'} overflow="auto">
      <table className="scl-table-grid">
        <thead
          style={{
            background: '#EDF2F7',
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        >
          <tr>
            {columns.map(col => (
              <th
                key={col.id}
                style={{
                  textAlign: col.align || 'left',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
                onClick={() => handleSort(col.id)}
              >
                <Flex
                  align="center"
                  justify={
                    col.align === 'right'
                      ? 'flex-end'
                      : col.align === 'center'
                      ? 'center'
                      : 'flex-start'
                  }
                  gap={1}
                >
                  {sortCol === col.id ? (
                    sortDir === 'asc' ? (
                      <BiSortUp size={14} />
                    ) : (
                      <BiSortDown size={14} />
                    )
                  ) : (
                    <BiSortAlt2 size={14} color="#A0AEC0" />
                  )}
                  <span>{col.label}</span>
                </Flex>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={`${row.id}-${row.produtoId}-${row.lojaId}-${i}`}
              style={
                row.alteradoSimulacao ? { background: '#FFFAF0' } : undefined
              }
            >
              {columns.map(col => (
                <td key={col.id} style={{ textAlign: col.align || 'left' }}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}

          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  textAlign: 'center',
                  padding: '32px',
                  color: '#A0AEC0',
                  fontSize: '13px'
                }}
              >
                Nenhum registro desta página corresponde à busca
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  )
}

export default PedidosCronogramaTable
export type { IRecomendacaoComprasRow }
